import {COLUMN_PAGING_POSITION, ROW_PAGING_POSITION} from '../focus/positions'
import type {BoardFocusPosition} from '../focus/types'
import {useBoardConfig, useBoardDataContext, useBoardPaging} from '../provider/contexts'
import type {PagingSentinelProps} from './use-paging-sentinel'
import {usePagingStop} from './use-paging-stop'

export type UseBoardAxisPagingResult = {
  /**
   * This axis has a further page. Mount the ghost when it is true and nothing when it is
   * false.
   *
   * False whenever the axis is truncated, whatever the caller stated, because a ghost
   * that pages a server which has stopped enumerating is a dead end.
   */
  hasMore: boolean
  /** A page for this axis is in flight. The other axis is unaffected either way. */
  isLoading: boolean
  /**
   * The server stopped enumerating this axis. Never true at the same time as `hasMore`,
   * because they are opposite advice: one is a reason to keep scrolling and the other is
   * a reason to narrow the search.
   */
  isTruncated: boolean
  /** The caller's name for this axis's paging control. */
  label: string
  /** The caller's end-of-board notice for this axis. */
  truncationLabel: string
  sentinelProps: PagingSentinelProps
}

type Axis = {
  hasMore: boolean
  isLoading: boolean
  isTruncated: boolean
  label: string
  truncationLabel: string
  onLoadMore: (() => void) | undefined
  /** Where this axis's ghost sits on the board. One per board, so one constant per axis. */
  position: BoardFocusPosition
}

/**
 * The half of an axis's paging that both axes do identically.
 *
 * Sharing it is what keeps the two from drifting. It does not make the axes one
 * direction: each one has its own callback, its own control and its own strings, and
 * reaching one never asks about the other. The duplicate-fetch guard is not here either,
 * for the same reason it is not repeated in `useBoardItemPaging`: it lives in
 * `usePagingSentinel`, which is the one mechanism all three directions already share.
 */
function useAxisPaging(axis: Axis): UseBoardAxisPagingResult {
  const hasMore = axis.hasMore && !axis.isTruncated
  const sentinelProps = usePagingStop({
    position: axis.position,
    label: axis.label,
    onReached: () => axis.onLoadMore?.(),
    enabled: hasMore && axis.onLoadMore !== undefined,
    // This axis's own loading state. The other axis paging leaves this sentinel free to
    // ask, because the page in flight is not the one it would be asking for.
    isLoading: axis.isLoading,
  })

  return {
    hasMore,
    isLoading: axis.isLoading,
    isTruncated: axis.isTruncated,
    label: axis.label,
    truncationLabel: axis.truncationLabel,
    sentinelProps,
  }
}

/**
 * More columns: the second of the board's three paging directions.
 *
 * Independent of the other two. Reaching the end of the columns asks for more columns
 * and says nothing about the rows or about any cell, and a board fetching more columns
 * puts nothing else into a loading state.
 */
export function useBoardColumnPaging(): UseBoardAxisPagingResult {
  const {data} = useBoardDataContext()
  const {labels} = useBoardConfig()
  const {onLoadMoreColumns} = useBoardPaging()

  return useAxisPaging({
    hasMore: data.hasMoreColumns,
    isLoading: data.isLoadingColumns ?? false,
    isTruncated: data.columnsTruncated ?? false,
    label: labels.loadMoreColumns,
    truncationLabel: labels.columnsTruncated,
    onLoadMore: onLoadMoreColumns,
    position: COLUMN_PAGING_POSITION,
  })
}

/**
 * More rows: the third of the board's three paging directions.
 *
 * A board with no swimlane has exactly one row and runs this same hook. It is not a
 * special path: such a board simply states `hasMoreRows: false`, which is the ordinary
 * way for any axis to say it is complete.
 */
export function useBoardRowPaging(): UseBoardAxisPagingResult {
  const {data} = useBoardDataContext()
  const {labels} = useBoardConfig()
  const {onLoadMoreRows} = useBoardPaging()

  return useAxisPaging({
    hasMore: data.hasMoreRows,
    isLoading: data.isLoadingRows ?? false,
    isTruncated: data.rowsTruncated ?? false,
    label: labels.loadMoreRows,
    truncationLabel: labels.rowsTruncated,
    onLoadMore: onLoadMoreRows,
    position: ROW_PAGING_POSITION,
  })
}
